import { Environment, environment } from "../constants/environment";
import type { FeConfig } from "@ceeferenderer/fe-renderer-sdk";
import type { LoginOptions } from "@dotcom/oauthjs";

type PickRequired<T, K extends keyof T> = Partial<Omit<T, K>> & Required<Pick<T, K>>;
type PartialItgConfig = PickRequired<FeConfig, "URL">;
type PartialOauthConfig = PickRequired<LoginOptions, "clientId">;
type Urls = Record<"xfinity-assistant" | "datalayer" | "logger" | "typeahead" | "serviceability" | "exitEvent", string>;

export type EnvironmentConfig = {
  appConfig: appConfig;
  itgConfig: PartialItgConfig;
  oauthConfig: PartialOauthConfig;
  queryParams?: Record<string, unknown>;
  urls: Urls;
};

type appConfig = {
  appDetails: appDetails;
  enablePendingConstruction: boolean;
  exitEventFlowId: string;
  serviceabilityFlowId: string;
  setJourneyFlowId: string;
};

type appDetails = {
  applicationName: string;
  cssApplicationName: string;
};

// URLs
const urlsBase = {
  "xfinity-assistant": "https://www.xfinity.com/xfinityassistant/",
};
const urlsProd: Urls = {
  ...urlsBase,
  datalayer: "https://static.cimcontent.net/data-layer/?appID=resi_support",
  exitEvent: "https://flowengine-ccp-prod.codebig2.net/flowExecute",
  logger: "https://api-support.xfinity.com/logger/",
  serviceability: "https://flowengine-ccp-prod.codebig2.net/flowExecute",
  typeahead: "https://retail360-location-api-prod.codebig2.net/api/addresses/search/byStreetAddress/smart",
};
const urlsPreProd: Urls = {
  ...urlsBase,
  datalayer: "https://static.cimcontent.net/data-layer/nonprod/?appID=resi_support",
  exitEvent: "https://flowengine-soak-uat.codebig2.net/flowExecute",
  logger: "https://api-support-preprod.xfinity.com/logger/",
  serviceability: "https://flowengine-soak-uat.codebig2.net/flowExecute",
  typeahead: "https://retail360-location-api-prod.codebig2.net/api/addresses/search/byStreetAddress/smart",
};
const urlsUat: Urls = {
  ...urlsBase,
  datalayer: "https://static.cimcontent.net/data-layer/nonprod/?appID=resi_support",
  exitEvent: "https://flowengine-uat.codebig2.net/flowExecute",
  logger: "https://api-support-test.xfinity.com/logger",
  serviceability: "https://flowengine-uat.codebig2.net/flowExecute",
  typeahead: "https://retail360-location-api-stg.codebig2.net/api/addresses/search/byStreetAddress/smart",
};
const urlsLower: Urls = {
  ...urlsBase,
  datalayer: "https://static.cimcontent.net/data-layer/nonprod/?appID=resi_support",
  exitEvent: "https://flowengine-design-uat.codebig2.net/flowExecute",
  logger: "https://api-support-test.xfinity.com/logger",
  serviceability: "https://flowengine-design-uat.codebig2.net/flowExecute",
  typeahead: "https://retail360-location-api-stg.codebig2.net/api/addresses/search/byStreetAddress/smart",
};

// ITG Config
const itgConfigLower = {
  // URL: "https://fe-r360-a609f52c-261f-415e-8e48-design-uat.codebig2.net",
  // URL: "https://itgprocessgateway-uat.codebig2.net",
  URL: "https://fe-r360-av-redesign-design-uat.codebig2.net",
  // URL: "https://maestro-nxgen-uat-vip.cable.comcast.com/itg-execution-int",
  // URL: "https://flowengine-ccp-uat-design-vip.cable.comcast.com/itg-execution/flowExecute?internalCall=true"
};
const itgConfigUat = {
  URL: "https://fe-r360-av-redesign-uat.codebig2.net",
  // URL: "https://fe-r360-a609f52c-261f-415e-8e48-design-uat.codebig2.net",
  // URL: "https://itgprocessgateway-uat.codebig2.net",
  // URL: "https://maestro-nxgen-uat-vip.cable.comcast.com/itg-execution",
  // URL: "https://maestro-nxgen-uat-vip.cable.comcast.com/itg-execution-int",
};

// ITG Config
const itgConfigPreprod = {
  // URL: "https://itgprocessgateway-uat.codebig2.net",
  // URL: "https://maestro-nxgen-uat-vip.cable.comcast.com/itg-execution",
  URL: "https://fe-r360-av-redesign-soak-uat.codebig2.net",
  // URL: "https://fe-r360-a609f52c-261f-415e-8e48-soak-uat.codebig2.net/",
};

// ITG Config
const itgConfigProduction = {
  //URL: "https://flowengine-prod.codebig2.net", // Direct endpoint
  //URL: "https://itgprocessgateway-prod.codebig2.net",
  //URL: "https://fe-r360-store-appointment-prod.codebig2.net",
  // URL: "https://fe-r360-av-redesign-prod.codebig2.net",
  URL: "https://fe-ccp-r360-av-redesign-prod.codebig2.net",
};

const appConfigData: appConfig = {
  appDetails: {
    applicationName: "Retail",
    cssApplicationName: "dataservice",
  },
  enablePendingConstruction: true,
  exitEventFlowId: "FE6092",
  serviceabilityFlowId: "FE6091",
  setJourneyFlowId: "FE15558",
};

const envConfigs: Record<Environment, EnvironmentConfig> = {
  dev: {
    appConfig: appConfigData,
    itgConfig: itgConfigLower,
    oauthConfig: {
      clientId: "retail360",
    },
    urls: urlsLower,
  },
  local: {
    appConfig: appConfigData,
    itgConfig: itgConfigLower,
    oauthConfig: {
      clientId: "retail360",
    },
    urls: urlsLower,
  },
  preproduction: {
    appConfig: appConfigData,
    itgConfig: itgConfigPreprod,
    oauthConfig: {
      clientId: "retail360",
    },
    urls: urlsPreProd,
  },
  production: {
    appConfig: appConfigData,
    itgConfig: itgConfigProduction,
    oauthConfig: {
      clientId: "retail360",
    },
    urls: urlsProd,
  },
  qa: {
    appConfig: appConfigData,
    itgConfig: itgConfigUat,
    oauthConfig: {
      clientId: "retail360",
    },
    urls: urlsUat,
  },
};

export const envConfig = envConfigs[environment];
